/**
 * P7(a) — parse a coach's free-text athlete-email entry (Screen B of the
 * "Set up your team" onboarding fork, and the team-page re-invite) into a
 * deduped list of valid, lowercased addresses.
 *
 * Accepts any mix of commas, whitespace, semicolons and newlines as
 * separators so a coach can paste a roster from a spreadsheet or type
 * "a@x.com, b@y.com". The server re-sanitizes + re-dedupes + caps — this is
 * purely for the live "Invite N athletes" count and to strip obvious junk
 * before the request.
 */

// Deliberately permissive (single dot-allowing regex, no TLD police) — the
// server is the authority; this only rejects clearly-not-an-email tokens.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * B443 — the most addresses ONE invite request carries. The server
 * (`TEAM_INVITE_MAX_PER_REQUEST` in `api/index.js`, pinned by a test that reads
 * its text) stops at the 50th valid address and drops the rest without a word,
 * so a screen that counted "Invite 120 athletes" was counting people the
 * request would never invite. The client says so and waits instead.
 */
export const MAX_INVITES_PER_REQUEST = 50;
/** The coach onboarding screen's sentence (rulings 2026-10-22 item 1): the rest are added later, from the team page. */
export const INVITE_CAP_SENTENCE =
  'Invite up to 50 athletes at a time, then add the rest from your team page.';
/**
 * Rulings 2026-10-22 item 13 — the same limit said inside the team page's own invite modal, where
 * "from your team page" is circular: the coach is already there and sends the next 50 in the same box.
 */
export const INVITE_CAP_SENTENCE_TEAM_PAGE = 'Invite up to 50 athletes at a time, then paste the rest.';

/** Where the box is: the onboarding screen (the default) or the team page's modal. */
export type InviteCapSurface = 'onboarding' | 'team-page';

/**
 * `null` when the list fits one request, otherwise the sentence for the surface. Called with no
 * surface it answers exactly as before (the onboarding sentence).
 */
export function inviteCapProblem(
  emails: readonly string[],
  surface: InviteCapSurface = 'onboarding',
): string | null {
  if (emails.length <= MAX_INVITES_PER_REQUEST) return null;
  return surface === 'team-page' ? INVITE_CAP_SENTENCE_TEAM_PAGE : INVITE_CAP_SENTENCE;
}

export function parseInviteEmails(raw: string): string[] {
  const tokens = String(raw ?? '')
    .split(/[\s,;]+/)
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
  const seen = new Set<string>();
  const out: string[] = [];
  for (const t of tokens) {
    if (!EMAIL_RE.test(t)) continue;
    if (t.length > 320) continue;
    if (seen.has(t)) continue;
    seen.add(t);
    out.push(t);
  }
  return out;
}
